import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient, useQueries, useInfiniteQuery } from '@tanstack/react-query';
import {
  api,
  EMAIL_KIND_CORRECTIONS_APPLIED,
  EMAIL_KIND_MOUNTING_COMPLETE,
  EMAIL_KIND_PRE_ARCHIVE_UPLOAD,
  EMAIL_KIND_PROJECT_REVIEWED,
  EMAIL_KIND_REVIEW_CHECK,
  type Project,
  type ProjectType,
} from '@/api/client';
import { useAuth } from '@/contexts/useAuth';
import { useToast } from '@/contexts/ToastContext';
import { Button } from '@/components/ui/Button/Button';
import { ProjectsTable } from '@/components/ProjectsTable/ProjectsTable';
import { useProjectsSearch } from '@/contexts/ProjectsSearchContext';
import { useProjectsColumnSearch } from '@/contexts/ProjectsColumnSearchContext';
import { ReviewCheckEmailModal } from '@/components/email/ReviewCheckEmailModal';
import { PreArchiveUploadEmailModal } from '@/components/email/PreArchiveUploadEmailModal';
import { MountingCompleteEmailModal } from '@/components/email/MountingCompleteEmailModal';
import { CorrectionsAppliedEmailModal } from '@/components/email/CorrectionsAppliedEmailModal';
import { ProjectReviewedEmailModal } from '@/components/email/ProjectReviewedEmailModal';
import { ProductionHandoffEmailModal } from '@/components/email/ProductionHandoffEmailModal';
import { isProjectExcludedFromActiveTable } from '@/utils/projectStatuses';
import { stageTransitionEmailKind, type StageEmailKind } from '@/utils/stageTransitionEmail';
import { getReadyByMonthBucketFromProject, parseYearMonthKey } from '@/utils/readyMonthStats';
import { HIGHLIGHT_PARAM, HIGHLIGHT_TYPE_PROJECT } from '@/hooks/useTeleportToTarget';
import { useReadyMonthSeparatorDisplay } from '@/hooks/useReadyMonthSeparatorDisplay';
import { useExpandedReadyMonths } from '@/hooks/useExpandedReadyMonths';
import { useExpandedArchive } from '@/hooks/useExpandedArchive';
import { markProjectUpdatedByMutation } from '@/lib/projectRealtimeDedup';
import {
  captureProjectListRollback,
  getActiveProjectsSnapshot,
  invalidateArchivedProjectCaches,
  invalidateReadyMonthProjectLists,
  mergeSingleProjectInCaches,
  patchProjectListCaches,
  setActiveProjectsSnapshot,
  syncArchivedCachesAfterProjectUpdated,
  syncReadyMonthCachesAfterProjectUpdated,
  syncProjectCollectionsSecondaryCaches,
  type ProjectListRollbackContext,
} from '@/lib/projectListQueryCache';
import {
  activeProjectIdsQueryOptions,
  archivedCountQueryOptions,
  archivedProjectIdsQueryOptions,
  readyMonthProjectIdsQueryOptions,
  readyMonthSummariesQueryOptions,
} from '@/lib/projectCollectionQueries';
import { rollbackProjectsAndReconcile } from '@/lib/projectMutationReconcile';
import { patchSessionsProjectFields } from '@/lib/sessionScheduleCache';
import {
  getProjectEntityFromCaches,
  mergeProjectsIntoSearchEntityCache,
  ARCHIVED_PROJECT_IDS_QUERY_KEY,
  PROJECTS_TABLE_ACTIVE_IDS_QUERY_KEY,
  projectEntityQueryKey,
} from '@/lib/projectEntityCache';
import {
  refreshUndoRedoAvailability,
  setLastUndoRedoProjectId,
} from '@/features/project-change-history/projectUndoRedoStore';
import {
  markProjectsTableHydrated,
  useProjectsTableSyncState,
} from '@/lib/projectsTableSyncPolicy';
import { parseSearchHighlightWords } from '@/utils/highlightSearchTerms';
import { sortProjectIdsLikeTableOrder } from '@/utils/sortProjectIdsLikeTableOrder';
import './ProjectsPage.css';

type ReviewEmailModalOpenState = {
  projectId: string;
  projectSnapshot: Project;
  mode: 'status_transition' | 'email_only';
  intendedState?: string;
};

/** Та же форма состояния, что и для письма «Проверка» — переход по статусу или отдельное письмо */
type PreArchiveEmailModalOpenState = ReviewEmailModalOpenState;
type MountingEmailModalOpenState = ReviewEmailModalOpenState;
type CorrectionsEmailModalOpenState = ReviewEmailModalOpenState;
type ProjectReviewedEmailModalOpenState = ReviewEmailModalOpenState;

type ProductionEmailModalOpenState = {
  projectId: string;
  projectSnapshot: Project;
};

function mutationErrMessage(err: unknown): string {
  return err instanceof Error ? err.message : String(err);
}

export function ProjectsPage() {
  const queryClient = useQueryClient();
  const { user } = useAuth();
  const { showSuccess, showError } = useToast();
  const [reviewEmailModal, setReviewEmailModal] = useState<ReviewEmailModalOpenState | null>(null);
  const [preArchiveEmailModal, setPreArchiveEmailModal] = useState<PreArchiveEmailModalOpenState | null>(null);
  const [mountingEmailModal, setMountingEmailModal] = useState<MountingEmailModalOpenState | null>(null);
  const [correctionsEmailModal, setCorrectionsEmailModal] = useState<CorrectionsEmailModalOpenState | null>(null);
  const [projectReviewedEmailModal, setProjectReviewedEmailModal] =
    useState<ProjectReviewedEmailModalOpenState | null>(null);
  const [productionEmailModal, setProductionEmailModal] = useState<ProductionEmailModalOpenState | null>(null);
  const { expandedReadyMonths, setExpandedReadyMonths } = useExpandedReadyMonths();
  const { expandedArchive, setExpandedArchive } = useExpandedArchive();
  const {
    monthSepDisplay,
    setMonthSepShowReadyTime,
    setMonthSepShowInProgress,
    setMonthSepShowProjectCount,
    setMonthSepShowActiveProjectsCount,
  } = useReadyMonthSeparatorDisplay();

  /** Ref для стабильного доступа к reviewEmailModal внутри useCallback без зависимости */
  const reviewEmailModalRef = useRef<ReviewEmailModalOpenState | null>(null);
  useEffect(() => {
    reviewEmailModalRef.current = reviewEmailModal;
  }, [reviewEmailModal]);

  const preArchiveEmailModalRef = useRef<PreArchiveEmailModalOpenState | null>(null);
  useEffect(() => {
    preArchiveEmailModalRef.current = preArchiveEmailModal;
  }, [preArchiveEmailModal]);

  const mountingEmailModalRef = useRef<MountingEmailModalOpenState | null>(null);
  useEffect(() => {
    mountingEmailModalRef.current = mountingEmailModal;
  }, [mountingEmailModal]);

  const correctionsEmailModalRef = useRef<CorrectionsEmailModalOpenState | null>(null);
  useEffect(() => {
    correctionsEmailModalRef.current = correctionsEmailModal;
  }, [correctionsEmailModal]);

  const projectReviewedEmailModalRef = useRef<ProjectReviewedEmailModalOpenState | null>(null);
  useEffect(() => {
    projectReviewedEmailModalRef.current = projectReviewedEmailModal;
  }, [projectReviewedEmailModal]);

  const { data: activeProjectIds = [], isLoading: activeLoading } = useQuery(
    activeProjectIdsQueryOptions(queryClient)
  );
  const syncState = useProjectsTableSyncState();

  const { debouncedSearch } = useProjectsSearch();
  const { debouncedColumnSearch, searchInFields } = useProjectsColumnSearch();
  const searchInKey = useMemo(() => [...searchInFields].sort().join(','), [searchInFields]);
  const navbarSearchActive = debouncedSearch.length > 0;
  const columnSearchActive =
    debouncedColumnSearch.length > 0 && searchInFields.length > 0;
  const tableSearchActive = navbarSearchActive || columnSearchActive;

  const searchHighlightWords = useMemo(() => {
    const fromNav = parseSearchHighlightWords(debouncedSearch);
    const fromCol = parseSearchHighlightWords(debouncedColumnSearch);
    return [...new Set([...fromNav, ...fromCol])];
  }, [debouncedSearch, debouncedColumnSearch]);

  const searchInfinite = useInfiniteQuery({
    queryKey: ['projects-search', debouncedSearch, debouncedColumnSearch, searchInKey] as const,
    enabled: tableSearchActive,
    initialPageParam: 0,
    queryFn: async ({ pageParam }) => {
      const raw = await api.projects.list({
        q: debouncedSearch || undefined,
        columnQ: debouncedColumnSearch || undefined,
        searchIn: columnSearchActive ? searchInKey : undefined,
        limit: 25,
        offset: pageParam,
      });
      const items = Array.isArray(raw) ? raw : raw.items;
      mergeProjectsIntoSearchEntityCache(queryClient, items);
      const hasMore = Array.isArray(raw) ? false : raw.hasMore;
      return {
        items,
        hasMore,
        nextOffset: pageParam + items.length,
      };
    },
    getNextPageParam: (last) => (last.hasMore ? last.nextOffset : undefined),
  });

  const searchProjectsById = useMemo(() => {
    const map = new Map<string, Project>();
    const pages = searchInfinite.data?.pages;
    if (!pages) return map;
    for (const page of pages) {
      for (const project of page.items) {
        map.set(project.id, project);
      }
    }
    return map;
  }, [searchInfinite.data]);

  const searchResultIds = useMemo(() => {
    const pages = searchInfinite.data?.pages;
    if (!pages?.length) return [];
    const out: string[] = [];
    const seen = new Set<string>();
    for (const page of pages) {
      for (const p of page.items) {
        if (!seen.has(p.id)) {
          seen.add(p.id);
          out.push(p.id);
        }
      }
    }
    return out;
  }, [searchInfinite.data]);

  const { data: monthStats = [], isLoading: monthsLoading } = useQuery(readyMonthSummariesQueryOptions());

  const { data: archivedCount = 0 } = useQuery(archivedCountQueryOptions());

  const {
    data: archivedProjectIds = [],
    isFetching: archivedProjectLoading,
  } = useQuery({
    ...archivedProjectIdsQueryOptions(queryClient),
    enabled: expandedArchive && archivedCount > 0,
  });

  useEffect(() => {
    if (archivedCount === 0) {
      queryClient.removeQueries({ queryKey: ARCHIVED_PROJECT_IDS_QUERY_KEY });
    }
  }, [archivedCount, queryClient]);

  const monthQueries = useQueries({
    queries: monthStats.map((stat) => {
      const { year, month, key } = parseYearMonthKey(stat.year_month);
      return {
        ...readyMonthProjectIdsQueryOptions(queryClient, year, month),
        enabled: !!expandedReadyMonths[key],
      };
    }),
  });

  /** Не зависеть от ссылки monthQueries — иначе лишние пересборки при любом re-render родителя */
  const readyDataEpoch = monthQueries.map((q) => q.dataUpdatedAt).join('|');
  const readyFetchEpoch = monthQueries.map((q) => (q.isFetching ? 1 : 0)).join('|');

  const resolveProject = useCallback(
    (projectId: string) => {
      if (tableSearchActive) {
        const fromSearch = searchProjectsById.get(projectId);
        if (fromSearch) return fromSearch;
      }
      return getProjectEntityFromCaches(queryClient, projectId);
    },
    [queryClient, tableSearchActive, searchProjectsById]
  );

  const [searchParams] = useSearchParams();
  const highlightParam = searchParams.get(HIGHLIGHT_PARAM);
  const teleportReadyProjectId = useMemo(() => {
    if (!highlightParam?.startsWith(`${HIGHLIGHT_TYPE_PROJECT}:`)) return null;
    return highlightParam.slice(HIGHLIGHT_TYPE_PROJECT.length + 1);
  }, [highlightParam]);

  const teleportHighlightProjectQuery = useQuery({
    queryKey: projectEntityQueryKey(teleportReadyProjectId || '__teleport_none__'),
    queryFn: () => api.projects.get(teleportReadyProjectId!),
    enabled:
      teleportReadyProjectId != null &&
      highlightParam != null &&
      highlightParam.startsWith(`${HIGHLIGHT_TYPE_PROJECT}:`),
    staleTime: 60_000,
  });

  const teleportProjectForMonthExpand = useMemo((): Project | undefined => {
    if (!teleportReadyProjectId) return undefined;
    return resolveProject(teleportReadyProjectId) ?? teleportHighlightProjectQuery.data;
  }, [teleportReadyProjectId, resolveProject, teleportHighlightProjectQuery.data]);

  useLayoutEffect(() => {
    if (!teleportReadyProjectId || monthStats.length === 0) return;
    const project = teleportProjectForMonthExpand;
    if (!project || !isProjectExcludedFromActiveTable(project.state)) return;
    const bucket = getReadyByMonthBucketFromProject(project);
    if (!bucket) return;
    const monthKey = `${bucket.year}-${String(bucket.month).padStart(2, '0')}`;
    if (!monthStats.some((s) => parseYearMonthKey(s.year_month).key === monthKey)) return;
    setExpandedReadyMonths((prev) => {
      if (prev[monthKey]) return prev;
      return { ...prev, [monthKey]: true };
    });
  }, [teleportReadyProjectId, teleportProjectForMonthExpand, monthStats, setExpandedReadyMonths]);

  useLayoutEffect(() => {
    if (!teleportReadyProjectId) return;
    const project = teleportProjectForMonthExpand;
    if (!project?.is_archived) return;
    setExpandedArchive(true);
  }, [teleportReadyProjectId, teleportProjectForMonthExpand, setExpandedArchive]);

  const readyProjectIdsByMonth = useMemo(() => {
    const out: Record<string, string[]> = {};
    monthStats.forEach((stat, i) => {
      const { key } = parseYearMonthKey(stat.year_month);
      if (!expandedReadyMonths[key]) return;
      const data = monthQueries[i]?.data;
      if (data) out[key] = data;
    });
    return out;
  // eslint-disable-next-line react-hooks/exhaustive-deps -- readyDataEpoch вместо нестабильной ссылки monthQueries
  }, [monthStats, expandedReadyMonths, readyDataEpoch]);

  const searchResultIdsTableOrdered = useMemo(() => {
    if (!tableSearchActive || searchResultIds.length === 0) return searchResultIds;
    return sortProjectIdsLikeTableOrder(searchResultIds, {
      readyMonthKeysInOrder: monthStats.map((stat) => parseYearMonthKey(stat.year_month).key),
      readyProjectIdsByMonth,
      activeProjectIds,
      archivedProjectIds,
      resolveProject,
    });
  }, [
    tableSearchActive,
    searchResultIds,
    monthStats,
    readyProjectIdsByMonth,
    activeProjectIds,
    archivedProjectIds,
    resolveProject,
  ]);

  const searchProjectsForFilter = useMemo(() => {
    if (!tableSearchActive) return [];
    return searchResultIdsTableOrdered
      .map((projectId) => resolveProject(projectId))
      .filter((p): p is Project => !!p);
  }, [tableSearchActive, searchResultIdsTableOrdered, resolveProject]);

  const activeProjects = useMemo(
    () =>
      activeProjectIds
        .map((projectId) => resolveProject(projectId))
        .filter((project): project is Project => !!project),
    [activeProjectIds, resolveProject]
  );

  const sortedActiveProjectIds = activeProjectIds;

  const readyProjectsByMonth = useMemo(() => {
    const out: Record<string, Project[]> = {};
    for (const [key, ids] of Object.entries(readyProjectIdsByMonth)) {
      out[key] = ids
        .map((projectId) => resolveProject(projectId))
        .filter((project): project is Project => !!project);
    }
    return out;
  }, [readyProjectIdsByMonth, resolveProject]);

  const readyMonthProjectLoading = useMemo(() => {
    const out: Record<string, boolean> = {};
    monthStats.forEach((stat, i) => {
      const { key } = parseYearMonthKey(stat.year_month);
      out[key] = !!monthQueries[i]?.isFetching;
    });
    return out;
  // eslint-disable-next-line react-hooks/exhaustive-deps -- readyFetchEpoch вместо нестабильной ссылки monthQueries
  }, [monthStats, readyFetchEpoch]);

  const toggleReadyMonth = useCallback((monthKey: string) => {
    setExpandedReadyMonths((prev) => ({ ...prev, [monthKey]: !prev[monthKey] }));
  }, []);

  const expandAllReadyMonths = useCallback(() => {
    setExpandedReadyMonths(
      Object.fromEntries(monthStats.map((s) => [parseYearMonthKey(s.year_month).key, true]))
    );
  }, [monthStats]);

  const collapseAllReadyMonths = useCallback(() => {
    setExpandedReadyMonths({});
  }, []);

  const toggleArchive = useCallback(() => {
    setExpandedArchive((prev) => !prev);
  }, [setExpandedArchive]);

  const updateStateMutation = useMutation({
    mutationFn: ({ id, state, changed_by_user_id }: { id: string; state: string; changed_by_user_id?: string | null }) =>
      api.projects.update(id, { state, changed_by_user_id }),
    onMutate: async ({ id, state }) => {
      const rollback = captureProjectListRollback(queryClient);
      await queryClient.cancelQueries({ queryKey: PROJECTS_TABLE_ACTIVE_IDS_QUERY_KEY });
      markProjectUpdatedByMutation(id);
      patchProjectListCaches(queryClient, (prev) =>
        prev ? prev.map((p) => (p.id === id ? { ...p, state } : p)) : prev
      );
      return rollback;
    },
    onError: (err, _vars, ctx: ProjectListRollbackContext | undefined) => {
      showError(mutationErrMessage(err));
      void rollbackProjectsAndReconcile(queryClient, ctx).catch(() => {});
    },
    onSuccess: (data) => {
      showSuccess('Статус обновлён');
      mergeSingleProjectInCaches(queryClient, data);
      setLastUndoRedoProjectId(data.id);
      void refreshUndoRedoAvailability(queryClient, data.id).catch(() => {});
      const arcSync = syncArchivedCachesAfterProjectUpdated(queryClient, data);
      invalidateArchivedProjectCaches(queryClient, {
        list: arcSync.archivedList,
        count: arcSync.invalidateArchivedCount,
      });
      if (isProjectExcludedFromActiveTable(data.state)) {
        const activeProjects = getActiveProjectsSnapshot(queryClient);
        if (activeProjects) {
          setActiveProjectsSnapshot(
            queryClient,
            activeProjects.filter((project) => project.id !== data.id)
          );
        }
        const sync = syncReadyMonthCachesAfterProjectUpdated(queryClient, data);
        if (sync.invalidateSummaries) {
          void queryClient.invalidateQueries({ queryKey: ['ready-month-summaries'] });
        }
        invalidateReadyMonthProjectLists(queryClient, sync.readyMonthLists);
      }
    },
  });

  const updateProjectTypeMutation = useMutation({
    mutationFn: ({ id, project_type }: { id: string; project_type: ProjectType | null }) =>
      api.projects.update(id, { project_type }),
    onMutate: async ({ id, project_type }) => {
      const rollback = captureProjectListRollback(queryClient);
      markProjectUpdatedByMutation(id);
      await queryClient.cancelQueries({ queryKey: PROJECTS_TABLE_ACTIVE_IDS_QUERY_KEY });
      patchProjectListCaches(queryClient, (prev) =>
        prev ? prev.map((p) => (p.id === id ? { ...p, project_type } : p)) : prev
      );
      patchSessionsProjectFields(queryClient, id, { project_type });
      return rollback;
    },
    onError: (err, { id }, ctx: ProjectListRollbackContext | undefined) => {
      showError(mutationErrMessage(err));
      const prevEntity = ctx?.projectEntities.find(([projectId]) => projectId === id)?.[1];
      if (prevEntity) {
        patchSessionsProjectFields(queryClient, id, {
          project_type: prevEntity.project_type ?? null,
        });
      }
      void rollbackProjectsAndReconcile(queryClient, ctx).catch(() => {});
    },
    onSuccess: (data) => {
      showSuccess('Тип проекта обновлён');
      mergeSingleProjectInCaches(queryClient, data);
      patchSessionsProjectFields(queryClient, data.id, {
        project_name: data.name ?? null,
        project_type: data.project_type ?? null,
      });
      setLastUndoRedoProjectId(data.id);
      void refreshUndoRedoAvailability(queryClient, data.id).catch(() => {});
      const arcSync = syncArchivedCachesAfterProjectUpdated(queryClient, data);
      invalidateArchivedProjectCaches(queryClient, {
        list: arcSync.archivedList,
        count: arcSync.invalidateArchivedCount,
      });
    },
  });

  const deleteMutation = useMutation({
    mutationFn: api.projects.delete,
    onMutate: async (id) => {
      const rollback = captureProjectListRollback(queryClient);
      await queryClient.cancelQueries({ queryKey: PROJECTS_TABLE_ACTIVE_IDS_QUERY_KEY });
      markProjectUpdatedByMutation(id);
      patchProjectListCaches(queryClient, (prev) =>
        prev ? prev.filter((p) => p.id !== id) : prev
      );
      return rollback;
    },
    onError: (err, _id, ctx: ProjectListRollbackContext | undefined) => {
      showError(mutationErrMessage(err));
      void rollbackProjectsAndReconcile(queryClient, ctx).catch(() => {});
    },
    onSuccess: () => {
      showSuccess('Проект удалён');
    },
  });

  const handleDelete = useCallback(
    (id: string) => {
      deleteMutation.mutate(id);
    },
    [deleteMutation]
  );

  const handleCloseReviewEmailModal = useCallback(() => {
    setReviewEmailModal(null);
  }, []);

  const handleClosePreArchiveEmailModal = useCallback(() => {
    setPreArchiveEmailModal(null);
  }, []);

  const handleOpenReviewCheckEmail = useCallback((project: Project) => {
    setReviewEmailModal({
      projectId: project.id,
      projectSnapshot: project,
      mode: 'email_only',
    });
  }, []);

  const handleOpenPreArchiveUploadEmail = useCallback((project: Project) => {
    setPreArchiveEmailModal({
      projectId: project.id,
      projectSnapshot: project,
      mode: 'email_only',
    });
  }, []);

  const handleCloseMountingEmailModal = useCallback(() => {
    setMountingEmailModal(null);
  }, []);

  const handleCloseCorrectionsEmailModal = useCallback(() => {
    setCorrectionsEmailModal(null);
  }, []);

  const handleCloseProjectReviewedEmailModal = useCallback(() => {
    setProjectReviewedEmailModal(null);
  }, []);

  const handleOpenProjectReviewedEmail = useCallback((project: Project) => {
    setProjectReviewedEmailModal({
      projectId: project.id,
      projectSnapshot: project,
      mode: 'email_only',
    });
  }, []);

  const handleOpenMountingCompleteEmail = useCallback((project: Project) => {
    setMountingEmailModal({
      projectId: project.id,
      projectSnapshot: project,
      mode: 'email_only',
    });
  }, []);

  const handleOpenCorrectionsAppliedEmail = useCallback((project: Project) => {
    setCorrectionsEmailModal({
      projectId: project.id,
      projectSnapshot: project,
      mode: 'email_only',
    });
  }, []);

  const handleCloseProductionEmailModal = useCallback(() => {
    setProductionEmailModal(null);
  }, []);

  const handleOpenProductionHandoffEmail = useCallback((project: Project) => {
    setProductionEmailModal({
      projectId: project.id,
      projectSnapshot: project,
    });
  }, []);

  /**
   * Читает актуальный проект напрямую из кэша React Query в момент вызова —
   * не захватывает снимки списков в замыкание, что делает функцию
   * стабильной и не ломает memo строк таблицы при обновлениях кэша.
   * Для модалок писем обязательно используйте resolveProject: useMemo(activeProjects)
   * не пересобирается при обновлении только ['project', id], иначе в модалку попадут
   * устаревшие объекты вместо актуальных данных из entity-кэша.
   */
  const handleStateChange = useCallback(
    (projectId: string, newState: string) => {
      const project =
        resolveProject(projectId) ??
        reviewEmailModalRef.current?.projectSnapshot ??
        preArchiveEmailModalRef.current?.projectSnapshot ??
        mountingEmailModalRef.current?.projectSnapshot ??
        correctionsEmailModalRef.current?.projectSnapshot ??
        projectReviewedEmailModalRef.current?.projectSnapshot;
      const emailKind = project ? stageTransitionEmailKind(project.state, newState) : null;
      if (project && emailKind) {
        const open: ReviewEmailModalOpenState = {
          projectId: project.id,
          projectSnapshot: project,
          mode: 'status_transition',
          intendedState: newState,
        };
        const openModal = {
          [EMAIL_KIND_REVIEW_CHECK]: setReviewEmailModal,
          [EMAIL_KIND_PRE_ARCHIVE_UPLOAD]: setPreArchiveEmailModal,
          [EMAIL_KIND_MOUNTING_COMPLETE]: setMountingEmailModal,
          [EMAIL_KIND_CORRECTIONS_APPLIED]: setCorrectionsEmailModal,
          [EMAIL_KIND_PROJECT_REVIEWED]: setProjectReviewedEmailModal,
        } satisfies Record<StageEmailKind, (state: ReviewEmailModalOpenState) => void>;
        openModal[emailKind](open);
        return;
      }
      updateStateMutation.mutate({ id: projectId, state: newState, changed_by_user_id: user?.id ?? undefined });
    },
    [resolveProject, updateStateMutation, user?.id]
  );

  const handleProjectTypeChange = useCallback(
    (projectId: string, newType: ProjectType | null) => {
      updateProjectTypeMutation.mutate({ id: projectId, project_type: newType });
    },
    [updateProjectTypeMutation]
  );

  const projectForReviewModal =
    reviewEmailModal != null
      ? (resolveProject(reviewEmailModal.projectId) ?? reviewEmailModal.projectSnapshot)
      : null;

  const projectForPreArchiveModal =
    preArchiveEmailModal != null
      ? (resolveProject(preArchiveEmailModal.projectId) ?? preArchiveEmailModal.projectSnapshot)
      : null;

  const projectForMountingModal =
    mountingEmailModal != null
      ? (resolveProject(mountingEmailModal.projectId) ?? mountingEmailModal.projectSnapshot)
      : null;

  const projectForCorrectionsModal =
    correctionsEmailModal != null
      ? (resolveProject(correctionsEmailModal.projectId) ?? correctionsEmailModal.projectSnapshot)
      : null;

  const projectForProjectReviewedModal =
    projectReviewedEmailModal != null
      ? (resolveProject(projectReviewedEmailModal.projectId) ?? projectReviewedEmailModal.projectSnapshot)
      : null;

  const projectForProductionModal =
    productionEmailModal != null
      ? (resolveProject(productionEmailModal.projectId) ?? productionEmailModal.projectSnapshot)
      : null;

  const archivedProjectsForFilter = useMemo(() => {
    if (!expandedArchive || archivedProjectIds.length === 0) return [];
    return archivedProjectIds
      .map((projectId) => resolveProject(projectId))
      .filter((p): p is Project => !!p);
  }, [expandedArchive, archivedProjectIds, resolveProject]);

  const isLoading = activeLoading || monthsLoading;
  const globalSearchFetching =
    tableSearchActive && (searchInfinite.isPending || searchInfinite.isFetching);
  const tableIsLoading = tableSearchActive
    ? globalSearchFetching && searchResultIds.length === 0
    : isLoading;

  useEffect(() => {
    if (activeLoading) return;
    if (activeProjectIds.length === 0) return;
    if (activeProjects.length > 0) return;
    void queryClient.invalidateQueries({ queryKey: PROJECTS_TABLE_ACTIVE_IDS_QUERY_KEY });
  }, [activeLoading, activeProjectIds, activeProjects.length, queryClient]);

  useEffect(() => {
    if (activeLoading || monthsLoading) return;
    markProjectsTableHydrated(queryClient);
    syncProjectCollectionsSecondaryCaches(queryClient);
  }, [activeLoading, monthsLoading, queryClient, readyDataEpoch, activeProjects]);

  return (
    <div className="projects-page">
      <div className="projects-page__table-wrap">
        {syncState.isReconciling ? (
          <div className="projects-page__loading" role="status" aria-live="polite">
            Синхронизация таблицы...
          </div>
        ) : null}
        {isLoading && <div className="projects-page__loading">Загрузка...</div>}
        <ProjectsTable
          isLoading={tableIsLoading}
          isSyncing={syncState.isReconciling}
          projectIds={isLoading ? [] : sortedActiveProjectIds}
          onDelete={handleDelete}
          onStateChange={handleStateChange}
          onProjectTypeChange={handleProjectTypeChange}
          onOpenReviewCheckEmail={handleOpenReviewCheckEmail}
          onOpenProductionHandoffEmail={handleOpenProductionHandoffEmail}
          onOpenPreArchiveUploadEmail={handleOpenPreArchiveUploadEmail}
          onOpenMountingCompleteEmail={handleOpenMountingCompleteEmail}
          onOpenCorrectionsAppliedEmail={handleOpenCorrectionsAppliedEmail}
          onOpenProjectReviewedEmail={handleOpenProjectReviewedEmail}
          deletingProjectId={
            deleteMutation.isPending && deleteMutation.variables !== undefined
              ? deleteMutation.variables
              : null
          }
          readyMonthStats={monthStats}
          expandedReadyMonths={expandedReadyMonths}
          onToggleReadyMonth={toggleReadyMonth}
          readyProjectIdsByMonth={readyProjectIdsByMonth}
          allProjectsForFilter={
            tableSearchActive
              ? searchProjectsForFilter
              : [...activeProjects, ...Object.values(readyProjectsByMonth).flat(), ...archivedProjectsForFilter]
          }
          globalSearchActive={tableSearchActive}
          globalSearchProjectIds={searchResultIdsTableOrdered}
          globalSearchProjectsById={tableSearchActive ? searchProjectsById : undefined}
          globalSearchFetching={globalSearchFetching}
          searchHighlightWords={tableSearchActive ? searchHighlightWords : undefined}
          readyMonthProjectLoading={readyMonthProjectLoading}
          monthSepDisplay={monthSepDisplay}
          onMonthSepShowReadyTime={setMonthSepShowReadyTime}
          onMonthSepShowInProgress={setMonthSepShowInProgress}
          onMonthSepShowProjectCount={setMonthSepShowProjectCount}
          onMonthSepShowActiveProjectsCount={setMonthSepShowActiveProjectsCount}
          onExpandAllReadyMonths={expandAllReadyMonths}
          onCollapseAllReadyMonths={collapseAllReadyMonths}
          archivedProjectCount={archivedCount}
          expandedArchive={expandedArchive}
          onToggleArchive={toggleArchive}
          archivedProjectIds={archivedProjectIds}
          archivedProjectLoading={archivedProjectLoading}
        />
        {tableSearchActive && searchInfinite.hasNextPage ? (
          <div className="projects-page__search-more">
            <Button
              type="button"
              variant="secondary"
              size="m"
              disabled={searchInfinite.isFetchingNextPage}
              onClick={() => void searchInfinite.fetchNextPage()}
            >
              {searchInfinite.isFetchingNextPage ? 'Загрузка…' : 'Показать ещё'}
            </Button>
          </div>
        ) : null}
      </div>
      {reviewEmailModal != null && projectForReviewModal != null ? (
        <ReviewCheckEmailModal
          open
          onClose={handleCloseReviewEmailModal}
          project={projectForReviewModal}
          mode={reviewEmailModal.mode}
          intendedState={reviewEmailModal.intendedState}
        />
      ) : null}
      {preArchiveEmailModal != null && projectForPreArchiveModal != null ? (
        <PreArchiveUploadEmailModal
          open
          onClose={handleClosePreArchiveEmailModal}
          project={projectForPreArchiveModal}
          mode={preArchiveEmailModal.mode}
          intendedState={preArchiveEmailModal.intendedState}
        />
      ) : null}
      {productionEmailModal != null && projectForProductionModal != null ? (
        <ProductionHandoffEmailModal
          open
          onClose={handleCloseProductionEmailModal}
          project={projectForProductionModal}
        />
      ) : null}
      {mountingEmailModal != null && projectForMountingModal != null ? (
        <MountingCompleteEmailModal
          open
          onClose={handleCloseMountingEmailModal}
          project={projectForMountingModal}
          mode={mountingEmailModal.mode}
          intendedState={mountingEmailModal.intendedState}
        />
      ) : null}
      {correctionsEmailModal != null && projectForCorrectionsModal != null ? (
        <CorrectionsAppliedEmailModal
          open
          onClose={handleCloseCorrectionsEmailModal}
          project={projectForCorrectionsModal}
          mode={correctionsEmailModal.mode}
          intendedState={correctionsEmailModal.intendedState}
        />
      ) : null}
      {projectReviewedEmailModal != null && projectForProjectReviewedModal != null ? (
        <ProjectReviewedEmailModal
          open
          onClose={handleCloseProjectReviewedEmailModal}
          project={projectForProjectReviewedModal}
          mode={projectReviewedEmailModal.mode}
          intendedState={projectReviewedEmailModal.intendedState}
        />
      ) : null}
    </div>
  );
}
